import React, { useState } from 'react';
import {
  Container, Box, Typography, Grid, Card, TextField, Button, Chip
} from '@mui/material';
import {
  Email, Phone, LocationOn, Chat, Send
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { websiteAPI } from '../../services/api';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import PhoneInput from '../../components/shared/PhoneInput';
import { useRegion } from '../../context/RegionContext';
import { useTranslation } from 'react-i18next';

const ContactPage = () => {
  const { t } = useTranslation();
  const seo = getPageSEO('contact');
  const { contact, phonePrefix } = useRegion();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    countryCode: phonePrefix,
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await websiteAPI.submitContactInquiry({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: `${formData.countryCode} ${formData.phone}`,
        subject: formData.subject,
        message: formData.message
      });
      enqueueSnackbar(t('website.contact.form.successMsg'), { variant: 'success' });
      setFormData({
        name: '', email: '', company: '',
        countryCode: formData.countryCode, phone: '',
        subject: '', message: ''
      });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to send message', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: <Email />,
      title: t('website.contact.methods.email.title'),
      value: contact.email,
      desc: t('website.contact.methods.email.desc')
    },
    {
      icon: <Phone />,
      title: t('website.contact.methods.phone.title'),
      value: contact.phone,
      desc: contact.hours
    },
    {
      icon: <LocationOn />,
      title: t('website.contact.methods.office.title'),
      value: contact.addressLine1,
      desc: contact.addressLine2
    },
    {
      icon: <Chat />,
      title: t('website.contact.methods.whatsapp.title'),
      value: contact.phone,
      desc: t('website.contact.methods.whatsapp.desc')
    }
  ];

  return (
    <WebsiteLayout>
      <SEO {...seo} />
      <HeroSection chip={t('website.contact.heroChip')}>
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
              {t('website.contact.heroTitle')}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, color: 'primary.contrastText' }}>
              {t('website.contact.heroSubtitle')}
            </Typography>
          </Box>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Grid container spacing={3}>
          {contactMethods.map((method, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                  boxShadow: theme => theme.palette.mode === 'dark' ? 'none' : '0 4px 20px rgba(0,0,0,0.08)',
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.15)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.contrastText',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {method.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {method.title}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                  {method.value}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {method.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
              {t('website.contact.form.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: 18 }}>
              {t('website.contact.form.subtitle')}
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('website.contact.form.name')}
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('website.contact.form.email')}
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('website.contact.form.company')}
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    countryCode={formData.countryCode}
                    phoneNumber={formData.phone}
                    onCountryCodeChange={(code) => setFormData({ ...formData, countryCode: code })}
                    onPhoneNumberChange={(phone) => setFormData({ ...formData, phone })}
                    label={t('website.contact.form.phone')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('website.contact.form.subject')}
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('website.contact.form.message')}
                    multiline
                    rows={6}
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                   <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    endIcon={loading ? null : <Send />}
                    sx={{
                      px: 4,
                      py: 2,
                      borderRadius: 2,
                      fontSize: 16,
                      fontWeight: 700,
                      bgcolor: 'primary.contrastText', // Forest Green
                      color: 'primary.main', // Bright Green
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: 'secondary.dark',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(22, 51, 0, 0.25)'
                      }
                    }}
                  >
                    {loading ? t('website.contact.form.sending') : t('website.contact.form.send')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                px: 4, py: 12,
                height: '100%',
                bgcolor: 'primary.contrastText', // Forest Green
                color: 'white'
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: 'white' }}>
                {t('website.contact.whyContact.title')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {t('website.contact.whyContact.sales.title')}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    {t('website.contact.whyContact.sales.body')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {t('website.contact.whyContact.support.title')}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    {t('website.contact.whyContact.support.body')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {t('website.contact.whyContact.partnerships.title')}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    {t('website.contact.whyContact.partnerships.body')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {t('website.contact.whyContact.training.title')}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    {t('website.contact.whyContact.training.body')}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{
        bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
        py: 12, mb: 0, mt: 8,
        height: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(https://images.unsplash.com/photo-1524661135-423995f22d0b?w=1200&h=400&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'

      }}>
        <Container maxWidth="xs">
          <Box>
            <Box sx={{ textAlign: 'center', bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)', p: 3, borderRadius: 2 }}>
              <LocationOn sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {contact.addressLine1}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {contact.addressLine2}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </WebsiteLayout>
  );
};

export default ContactPage;
